import { AddTaskFormTypes, AddTaskProps } from '@/components/Modals/AddTask/AddTask.model.ts';
import type { CreateStudyAction, CreateStudyState } from '@/components/Studies/Create/Create.model';
import { TaskSpecIssuesModal } from '@/components/Studies/Create/Steps/Tasks/TaskSpecIssuesModal.tsx';
import {
  convertTaskSpecScheduleToIsoDate,
  TaskSpecIssueAction,
  TaskSpecV2Issue,
  TaskSpecWrapper,
  validateTaskSpec,
} from '@/components/Studies/Create/Steps/Tasks/TasksV2.util.ts';
import { TaskScheduleFrequencyCell } from '@/components/Studies/Settings/Settings.utils';
import { taskTypes } from '@/components/Studies/Tasks/Task';
import { TimezoneSelect } from '@/components/TimezoneSelect/TimezoneSelect.tsx';
import Button from '@/components/UI/Button';
import Indicator from '@/components/UI/Indicator';
import Link from '@/components/UI/Link';
import SelectDropDown from '@/components/UI/SelectDropDown';
import type { ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import UserAvatar from '@/components/UI/UserAvatar';
import { errorToast, successToast, warningToast } from '@/helpers.tsx';
import { _isNil, _isNotEmpty, _notNil, uuid } from '@/littledash';
import InVivoError from '@/model/InVivoError.ts';
import { Observation } from '@/model/Observation.model.ts';
import { TeamGlossary } from '@/model/Glosary.model.ts';
import type { State } from '@/model/State.model';
import type { TaskSchedule, TaskSpecCreate } from '@/model/Task.model';
import type { MainUser, StudyUser, UserApiId } from '@/model/User.model';
import useMountedState from '@/support/Hooks/fetch/useMountedState.ts';
import { ApiService } from '@/support/ApiService';
import { LegacyApiService } from '@/support/LegacyApiService.ts';
import { featuresSelector, timeZonesSelector } from '@/support/Selectors.tsx';
import { DateRenderFormat, DateUtils } from '@/utils/Date.utils.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import { useModalAction } from '@/utils/modal';
import { createSelector } from '@reduxjs/toolkit';
import { Dispatch, FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { RiAlertFill } from 'react-icons/ri';
import ReactModal from 'react-modal';
import { useSelector } from 'react-redux';
import { TaskSpecTimeCell } from '@/components/Tasks/TaskSpecTimeCell/TaskSpecTimeCell';
import { useStudyCreateStep, useStudyCreateGroups } from '@/utils/studyCreation/useStudyCreate.tsx';
import { getTargetNameForTasks } from '@/components/Tasks/Show/Tasks.utils.ts';

interface TaskV2Props {
  state: CreateStudyState;
  dispatch: Dispatch<CreateStudyAction>;
  isSummary?: boolean;
}

interface TaskSpecsBulkActionsProps {
  selectedTaskSpecs: Array<TaskSpecWrapper>;
  dispatch: TaskV2Props['dispatch'];
}

const TaskSpecsBulkActions: FC<TaskSpecsBulkActionsProps> = ({ selectedTaskSpecs, dispatch }) => {
  const selectedTaskSpecCount = selectedTaskSpecs.length;
  const hasSelectedTaskSpecs = selectedTaskSpecCount > 0;
  const actions: Array<ActionProps> = [
    {
      name: 'Duplicate',
      key: 'duplicate',
      action: () =>
        dispatch({
          type: 'add-task',
          data: selectedTaskSpecs.reduce(
            (acc, { taskSpec }) => ({
              ...acc,
              [uuid()]: { ...taskSpec, title: `${taskSpec.title ?? 'untitled'} (duplicate)` },
            }),
            {}
          ),
        }),
    },
    {
      name: 'Delete',
      key: 'delete',
      className: 'red',
      action: () => dispatch({ type: 'delete-task', data: selectedTaskSpecs.map((s) => s.id) }),
    },
  ];
  return (
    <div className="w-100 flex items-center mt3">
      {hasSelectedTaskSpecs && (
        <div
          className="mid-gray bg-light-gray ph3 ba b--moon-gray f6 br-0 br--left br1"
          style={{
            whiteSpace: 'nowrap',
            lineHeight: '2.4rem',
          }}
        >
          {selectedTaskSpecCount} selected
        </div>
      )}
      <SelectDropDown
        title="Bulk actions"
        className={`plain f6 ${hasSelectedTaskSpecs ? 'br--right' : ''}`}
        alignMenu="right"
        disabled={!hasSelectedTaskSpecs}
      >
        <ActionList actions={actions} />
      </SelectDropDown>
    </div>
  );
};

const selector = createSelector(
  [
    (state: State) => state.user.currentUser,
    (state: State) => state.team.team.users,
    timeZonesSelector,
    featuresSelector,
  ],
  (currentUser, teamUsers, timezones, features) => {
    const { activeUsers, teamUserMap } = teamUsers.reduce<{
      activeUsers: Array<MainUser>;
      teamUserMap: Map<UserApiId, MainUser>;
    }>(
      (acc, user) => {
        if (user.pivot.status === 'active') {
          acc.activeUsers.push(user);
          acc.teamUserMap.set(user.api_id, user);
        }
        return acc;
      },
      { activeUsers: [], teamUserMap: new Map() }
    );
    return {
      currentUser,
      teamUsers: activeUsers,
      teamUserMap,
      timezones,
      studyExecutionTimezoneEnabled: features?.study_execution_timezone ?? false,
    };
  }
);
type GlossaryData = { samples: Array<TeamGlossary>; observations: Array<Observation> };
export const TasksV2: FC<TaskV2Props> = ({ state, dispatch, isSummary = false }) => {
  const { openModal } = useModalAction();
  const { currentUser, teamUsers, teamUserMap, timezones, studyExecutionTimezoneEnabled } = useSelector(selector);
  const [displayIssuesModal, setDisplayIssuesModal] = useState(false);
  const isMounted = useMountedState();
  const { groups, treatments } = useStudyCreateGroups();
  const [glossaryData, updateGlossaryData] = useState<GlossaryData>(() => ({
    samples: [],
    observations: [],
  }));
  const { stepReady, setStepReady } = useStudyCreateStep();

  const { studyUsers } = useMemo(() => {
    const studyUserMap = new Map(
      [
        ...(_isNil(state?.study?.author) ? [] : [state.study.author]),
        ...(state?.study?.users ?? []),
        ...(_isNil(currentUser) ? [] : [currentUser]),
      ].map((user) => [user.api_id, user as StudyUser])
    );
    return { studyUsers: Array.from(studyUserMap.values()).sort((a, b) => a.name.localeCompare(b.name)), studyUserMap };
  }, [currentUser, state]);

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const { taskSpecs, taskSpecTitleMap } = useMemo<{
    taskSpecs: Array<TaskSpecWrapper>;
    taskSpecTitleMap: Map<string, string>;
  }>(() => {
    const taskSpecs = Object.entries(state.study.tasks ?? {}).map(([id, taskSpec]) => {
      if (taskSpec.schedule.type !== 'conditional') {
        taskSpec.schedule = convertTaskSpecScheduleToIsoDate(taskSpec.schedule, state.study.timezone ?? undefined);
      }
      return {
        id,
        taskSpec,
      };
    });
    const taskSpecTitleMap = new Map(taskSpecs.map((tsw) => [tsw.id, tsw.taskSpec.title as string]));
    return { taskSpecs, taskSpecTitleMap };
  }, [state]);

  const taskSpecIssues = useMemo<Map<TaskSpecWrapper['id'], Array<TaskSpecV2Issue>>>(() => {
    const userApiIds = new Set([currentUser.api_id, ...state.study.users.map((u) => u.api_id)]);
    const groupTempIds = groups.reduce(
      (acc, group) => (_notNil(group.temp_id) ? acc.add(group.temp_id) : acc),
      new Set<string>()
    );

    const sampleTempIds = glossaryData.samples.reduce(
      (acc, sample) => (_notNil(sample.api_id) ? acc.add(sample.api_id) : acc),
      new Set<string>()
    );

    const observationIds = new Set<number>(glossaryData.observations.map((observation) => Number(observation.id)));

    const treatmentTempIds = treatments.reduce(
      (acc, treatment) => (_notNil(treatment.temp_id) ? acc.add(`${treatment.temp_id}`) : acc),
      new Set<string>()
    );

    const measurementIds = new Set<string>(
      state.form.presets.find((p) => p.id === state.study.preset_id)?.calculations?.map((c) => c.id) ?? []
    );

    return validateTaskSpec(
      taskSpecs,
      userApiIds,
      groupTempIds,
      sampleTempIds,
      observationIds,
      treatmentTempIds,
      measurementIds
    );
  }, [taskSpecs, state, currentUser, glossaryData]);

  const selectedTaskSpecs = useMemo<Array<TaskSpecWrapper>>(
    () =>
      Object.entries(selectedRows).reduce<Array<TaskSpecWrapper>>((acc, [rowIndex, selected]) => {
        const taskSpec = taskSpecs?.[Number(rowIndex)];
        if (selected && _notNil(taskSpec)) {
          acc.push(taskSpec);
        }
        return acc;
      }, []),
    [taskSpecs, selectedRows]
  );

  useEffect(() => {
    const onError = { toast: false, capture: true, throw: false };

    Promise.all([
      ApiService.call({
        endpoint: 'GET /api/v1/team-glossary',
        path: undefined,
        query: {
          group: 'samples',
          perPage: -1,
          filterType: 'and',
          'filters[]': 'status|eq|active',
          order: 'asc',
        },
        options: { onError: { ...onError, slug: 'samples-glossary-load' } },
      }),
      LegacyApiService.call({
        method: 'GET',
        apiRoute: 'team-glossary.list',
        query: { group: 'observations' },
        options: { onError: { ...onError, slug: 'observations-glossary-load' } },
      }),
    ])
      .then(([samplesResponse, observationsResponse]) => {
        if (isMounted()) {
          const samples: Array<TeamGlossary> = (samplesResponse.body as { data: Array<TeamGlossary> })?.data ?? [];
          const observations: Array<Observation> =
            (observationsResponse.body as { data: Array<Observation> })?.data ?? [];
          updateGlossaryData({ samples, observations });
        }
      })
      .catch((cause) => {
        ExceptionHandler.captureException(
          new InVivoError('Could not load sample & observation glossary', {
            cause: cause,
            slug: 'tasks-glossary-load',
          })
        );
      });
  }, [isMounted, updateGlossaryData]);

  const handleTaskSpecCreateOrUpdate = (id: string, taskSpec: TaskSpecCreate) => {
    dispatch({ type: 'add-task', data: { [id]: taskSpec } });
  };
  const handleStudyExecutionTimezoneUpdate = (timezone?: string) => {
    dispatch({ type: 'update-timezone', data: timezone ?? DateUtils.timezone() });
  };

  const handleAddClick = () => {
    const execution: AddTaskProps['execution'] = {
      measurements: (state.form.presets.find((p) => p.id === state.study.preset_id)?.calculations ?? [])
        .reduce<Array<{ id: string; title: string }>>(
          (acc, calculation) =>
            _isNotEmpty(calculation.measurements)
              ? [
                  ...acc,
                  {
                    id: calculation.id,
                    title: calculation.name,
                  },
                ]
              : acc,
          []
        )
        .sort((a, b) => a.title.localeCompare(b.title)),
      observations: glossaryData.observations
        .map(({ id, title }) => ({ id: Number(id), title }))
        .sort((a, b) => a.title.localeCompare(b.title)),
      samples: glossaryData.samples
        .map(({ api_id, title }) => ({ id: api_id, title: title ?? '' }))
        .sort((a, b) => a.title.localeCompare(b.title)),
      treatments: treatments
        .map((t) => {
          return {
            id: `${t.temp_id}`,
            title: `${t.treatment_label}`,
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title)),
    };

    openModal('ADD_TASK', {
      studyStartedOn: state?.study?.started_on,
      studyCreationUsers: studyUsers,
      studyCreationGroups: groups ?? [],
      execution,
      handleSubmit: (taskSpec: TaskSpecCreate) => handleTaskSpecCreateOrUpdate(uuid(), taskSpec),
    });
  };

  const handleTaskSpecClick = async (
    event: MouseEvent<HTMLAnchorElement> | undefined,
    { id, taskSpec }: TaskSpecWrapper,
    initialTab?: AddTaskFormTypes
  ) => {
    event?.preventDefault();
    const execution: AddTaskProps['execution'] = {
      measurements: (state.form.presets.find((p) => p.id === state.study.preset_id)?.calculations ?? [])
        .reduce<Array<{ id: string; title: string }>>(
          (acc, calculation) =>
            _isNotEmpty(calculation.measurements)
              ? [
                  ...acc,
                  {
                    id: calculation.id,
                    title: calculation.name,
                  },
                ]
              : acc,
          []
        )
        .sort((a, b) => a.title.localeCompare(b.title)),
      observations: glossaryData.observations
        .map(({ id, title }) => ({ id: Number(id), title }))
        .sort((a, b) => a.title.localeCompare(b.title)),
      samples: glossaryData.samples
        .map(({ api_id, title }) => ({ id: api_id, title: title ?? '' }))
        .sort((a, b) => a.title.localeCompare(b.title)),
      treatments: treatments
        .map((t) => {
          return {
            id: `${t.temp_id}`,
            title: `${t.treatment_label}`,
          };
        })
        .sort((a, b) => a.title.localeCompare(b.title)),
    };
    openModal('EDIT_TASK', {
      studyCreationTaskSpec: taskSpec,
      studyCreationUsers: studyUsers,
      teamUsers: teamUsers,
      studyCreationGroups: groups ?? [],
      execution,
      handleSubmit: (studyCreateTaskSpec: TaskSpecCreate) => handleTaskSpecCreateOrUpdate(id, studyCreateTaskSpec),
      initialTab,
      studyTimezone: state.study?.timezone ?? null,
    });
  };

  const columns = useMemo<Array<TanStackColumnDef<TaskSpecWrapper>>>(
    () => [
      {
        id: 'title',
        header: 'Title',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) =>
          isSummary ? (
            (original.taskSpec.title ?? '-')
          ) : (
            <Link className="link blue" onClick={(e) => handleTaskSpecClick(e, original)}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {taskSpecIssues.has(original.id) && <Indicator smallIndicator indicatorClassName="mr2" />}
                <span>{original.taskSpec.title ?? '-'}</span>
              </div>
            </Link>
          ),
      },
      {
        id: 'description',
        header: 'Description',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) => (
          <p className="truncate">{original.taskSpec.description ?? '-'}</p>
        ),
      },
      {
        id: 'type',
        header: 'Type',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) => taskTypes[original.taskSpec?.type],
      },
      {
        id: 'target',
        header: 'Target',
        size: 75,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) =>
          original.taskSpec.target?.type === 'group' ? (
            <span
              onClick={() =>
                openModal('TASK_TARGET_DETAILS', {
                  target: original.taskSpec.target,
                  groups: groups,
                  studyId: null,
                  taskTitle: original.taskSpec.title,
                })
              }
              className="link blue"
            >
              {getTargetNameForTasks(original.taskSpec.target)}
            </span>
          ) : (
            <span>{getTargetNameForTasks(original.taskSpec.target)}</span>
          ),
      },
      {
        id: 'frequency',
        header: 'Frequency',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) => (
          <TaskScheduleFrequencyCell schedule={original.taskSpec.schedule as TaskSchedule} />
        ),
      },
      {
        id: 'start',
        header: 'Start',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) =>
          original.taskSpec.schedule.type === 'conditional'
            ? '-'
            : DateUtils.renderDate(original.taskSpec.schedule.duration.start, {
                format: DateRenderFormat.Date,
              }),
      },
      {
        id: 'end',
        header: 'End',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) =>
          original.taskSpec.schedule.type === 'conditional'
            ? '-'
            : DateUtils.renderDate(original.taskSpec.schedule.duration.end, {
                format: DateRenderFormat.Date,
              }),
      },
      {
        id: 'time',
        header: 'Time',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) => (
          <TaskSpecTimeCell time={original.taskSpec?.schedule?.time} />
        ),
      },
      {
        id: 'assignees',
        header: 'Assignees',
        size: 75,
        isVisible: true,
        enableSorting: true,
        cell: ({ row: { original } }: { row: { original: TaskSpecWrapper } }) => (
          <div>
            {(original.taskSpec.assignees ?? []).map((userId) => {
              const user = teamUserMap.get(userId);
              return <UserAvatar key={userId} user={user} style={{ marginRight: '3px' }} tooltip={user?.name} />;
            })}
          </div>
        ),
      },
    ],
    [handleTaskSpecClick, isSummary, taskSpecIssues, teamUserMap, groups]
  );

  useEffect(() => {
    const valid = (taskSpecIssues?.size ?? 0) === 0;
    if (stepReady !== valid) {
      setStepReady((taskSpecIssues?.size ?? 0) === 0);
    }
  }, [taskSpecIssues, setStepReady, stepReady]);
  const timezoneValidation = useMemo<string | null>(() => {
    if (_notNil(state.study.timezone) && !timezones.includes(state.study.timezone)) {
      return `Your timezone ${state.study.timezone} is not available. If an appropriate alternative cannot be selected, please contact support`;
    }
    return null;
  }, [state.study.timezone, timezones]);

  const handleTaskSpecIssueAction = (action: TaskSpecIssueAction) => {
    switch (action.type) {
      case 'close-modal':
        setDisplayIssuesModal(false);
        break;
      case 'open-task-spec': {
        setDisplayIssuesModal(false);
        const taskSpec = taskSpecs.find((t) => t.id === action.taskSpecId);
        if (_notNil(taskSpec)) {
          handleTaskSpecClick(undefined, taskSpec, action.tab);
        } else {
          errorToast('Could not open task spec');
          setDisplayIssuesModal(false);
        }
        break;
      }
      case 'add-invalid-users': {
        const usersToAdd = action.users.reduce<Array<StudyUser>>((acc, userId) => {
          const user = teamUserMap.get(userId);
          if (_notNil(user)) {
            acc.push({
              id: user.id,
              api_id: user.api_id,
              name: user.name,
              email: user.email,
              access: action.access,
            } as StudyUser);
          }
          return acc;
        }, []);
        if (usersToAdd.length > 0) {
          dispatch({ type: 'updateStudy', data: { users: [...state.study.users, ...usersToAdd] } });
          successToast(
            <>
              <p>
                {usersToAdd.length} assignee{usersToAdd.length > 0 ? 's' : ''} added to study with {action.access}{' '}
                access
              </p>
              <ul className="pt1 fw1">
                {usersToAdd.map((u) => (
                  <li key={u.api_id}>{u.name}</li>
                ))}
              </ul>
            </>
          );
        } else {
          errorToast('Could not add assignee(s) to study');
          setDisplayIssuesModal(false);
        }
        break;
      }
      case 'remove-invalid-users': {
        const issueTaskSpec = taskSpecs.find((t) => t.id === action.taskSpecId);
        const taskSpecsToUpdate = action.allTasks ? taskSpecs : _isNil(issueTaskSpec) ? [] : [issueTaskSpec];
        if (taskSpecsToUpdate.length > 0) {
          const userIdsToRemove = new Set(action.users);
          const removedUsers = Array.from(userIdsToRemove.values()).reduce<Array<MainUser>>((acc, userId) => {
            if (teamUserMap.has(userId)) {
              acc.push(teamUserMap.get(userId) as MainUser);
            }
            return acc;
          }, []);

          dispatch({
            type: 'add-task',
            data: taskSpecsToUpdate.reduce<Record<TaskSpecWrapper['id'], TaskSpecWrapper['taskSpec']>>(
              (acc, taskSpec) => {
                return {
                  ...acc,
                  [taskSpec.id]: {
                    ...taskSpec.taskSpec,
                    assignees: (taskSpec.taskSpec.assignees ?? []).filter((userId) => !userIdsToRemove.has(userId)),
                  },
                };
              },
              {}
            ),
          });
          successToast(
            <>
              <p>
                {userIdsToRemove.size} assignee{userIdsToRemove.size > 0 ? 's' : ''} removed from{' '}
                {taskSpecsToUpdate.length > 1 ? `${taskSpecsToUpdate.length} tasks` : 'task'}
              </p>
              <ul className="pt1 fw1">
                {removedUsers.map((u) => (
                  <li key={u.api_id}>{u.name}</li>
                ))}
              </ul>
            </>
          );
        }
        break;
      }
      case 'remove-invalid-groups-and-open': {
        const taskSpec = taskSpecs.find((t) => t.id === action.taskSpecId);
        if (_notNil(taskSpec) && taskSpec.taskSpec.target.type === 'group') {
          const groupIdsToRemove = new Set(action.groups);
          const updatedGroupList = taskSpec.taskSpec.target.groups.filter((groupId) => !groupIdsToRemove.has(groupId));
          const updatedTaskSpecWrapper: TaskSpecWrapper = {
            ...taskSpec,
            taskSpec: {
              ...taskSpec.taskSpec,
              target: updatedGroupList.length === 0 ? { type: 'animal' } : { type: 'group', groups: updatedGroupList },
            },
          };
          handleTaskSpecCreateOrUpdate(action.taskSpecId, updatedTaskSpecWrapper.taskSpec);
          handleTaskSpecClick(undefined, updatedTaskSpecWrapper, 'target');
          if (updatedGroupList.length > 0) {
            successToast(`${groupIdsToRemove.size} group${groupIdsToRemove.size > 0 ? 's' : ''} removed`);
          } else {
            warningToast(
              <>
                <p>Task target has been changed to all animals</p>
                <p className="pt1 fw1">
                  No groups existed after removing {groupIdsToRemove.size} group{groupIdsToRemove.size > 0 ? 's' : ''}
                </p>
              </>
            );
          }
        } else {
          errorToast('Could not remove group(s)');
        }
        setDisplayIssuesModal(false);
        break;
      }
      case 'remove-invalid-samples-and-open': {
        const taskSpec = taskSpecs.find((t) => t.id === action.taskSpecId);
        if (_notNil(taskSpec) && taskSpec.taskSpec.type === 'sample') {
          const samplesToRemove = new Set(action.samples);
          const updatedSampleList = (taskSpec.taskSpec.execution?.sample?.samples ?? []).filter(
            (sample) => !samplesToRemove.has(String(sample))
          );
          const updatedTaskSpecWrapper: TaskSpecWrapper = {
            ...taskSpec,
            taskSpec: {
              ...taskSpec.taskSpec,
              execution: {
                ...taskSpec.taskSpec.execution,
                type: 'workflow',
                sample: {
                  ...taskSpec.taskSpec.execution?.sample,
                  auto_generate_sample_id: taskSpec.taskSpec.execution?.sample?.auto_generate_sample_id ?? false,
                  after_save_action: taskSpec.taskSpec.execution?.sample?.after_save_action ?? 'next_animal',
                  samples: updatedSampleList,
                },
                measurement: taskSpec.taskSpec.execution?.measurement ?? null,
                observation: taskSpec.taskSpec.execution?.observation ?? null,
                dosing: taskSpec.taskSpec.execution?.dosing ?? null,
              },
            },
          };
          handleTaskSpecCreateOrUpdate(action.taskSpecId, updatedTaskSpecWrapper.taskSpec);
          handleTaskSpecClick(undefined, updatedTaskSpecWrapper, 'execution');
          if (updatedSampleList.length > 0) {
            successToast(`${samplesToRemove.size} sample${samplesToRemove.size > 0 ? 's' : ''} removed`);
          }
        } else {
          errorToast('Could not remove sample(s)');
        }
        setDisplayIssuesModal(false);
        break;
      }
      case 'remove-invalid-measurements-and-open': {
        const taskSpec = taskSpecs.find((t) => t.id === action.taskSpecId);
        if (
          _notNil(taskSpec) &&
          taskSpec.taskSpec.type === 'measurement' &&
          _notNil(taskSpec.taskSpec.execution?.measurement)
        ) {
          const measurementsToRemove = new Set(action.measurements);
          const updatedMeasuremntList = (taskSpec.taskSpec.execution?.measurement?.measurements ?? []).filter(
            (measurement) => !measurementsToRemove.has(measurement)
          );
          const updatedTaskSpecWrapper: TaskSpecWrapper = {
            ...taskSpec,
            taskSpec: {
              ...taskSpec.taskSpec,
              execution: {
                ...taskSpec.taskSpec.execution,
                type: 'workflow',
                measurement: {
                  weight_percentage_change:
                    taskSpec.taskSpec.execution?.measurement?.weight_percentage_change ?? 'first_measurement',
                  assign_identifier: taskSpec.taskSpec.execution?.measurement?.assign_identifier ?? null,
                  after_save_action: taskSpec.taskSpec.execution?.measurement?.after_save_action ?? 'next_animal',
                  cursor_position: taskSpec.taskSpec.execution?.measurement?.cursor_position ?? null,
                  measurements: updatedMeasuremntList,
                },
                sample: taskSpec.taskSpec.execution?.sample ?? null,
                observation: taskSpec.taskSpec.execution?.observation ?? null,
                dosing: taskSpec.taskSpec.execution?.dosing ?? null,
              },
            },
          };
          handleTaskSpecCreateOrUpdate(action.taskSpecId, updatedTaskSpecWrapper.taskSpec);
          handleTaskSpecClick(undefined, updatedTaskSpecWrapper, 'execution');
          if (updatedMeasuremntList.length > 0) {
            successToast(`${measurementsToRemove.size} measurement${measurementsToRemove.size > 0 ? 's' : ''} removed`);
          }
        } else {
          errorToast('Could not remove measurement(s)');
        }
        setDisplayIssuesModal(false);
        break;
      }
      case 'remove-invalid-treatments-and-open': {
        const taskSpec = taskSpecs.find((t) => t.id === action.taskSpecId);
        if (_notNil(taskSpec) && taskSpec.taskSpec.type === 'dosing' && _notNil(taskSpec.taskSpec.execution?.dosing)) {
          const treatmentsToRemove = new Set(action.treatments);
          const updatedTreatmentList = (taskSpec.taskSpec.execution?.dosing?.treatments ?? []).filter(
            (treatment) => !treatmentsToRemove.has(`${treatment}`)
          );
          const updatedTaskSpecWrapper: TaskSpecWrapper = {
            ...taskSpec,
            taskSpec: {
              ...taskSpec.taskSpec,
              execution: {
                ...taskSpec.taskSpec.execution,
                type: 'workflow',
                dosing: {
                  skip_dosing_for_unrequired_animals:
                    taskSpec.taskSpec.execution?.dosing?.skip_dosing_for_unrequired_animals ?? false,
                  use_new_body_weight: taskSpec.taskSpec.execution?.dosing?.use_new_body_weight ?? false,
                  after_save_action: taskSpec.taskSpec.execution?.dosing?.after_save_action ?? 'next_animal',
                  treatments: updatedTreatmentList,
                },
                sample: taskSpec.taskSpec.execution?.sample ?? null,
                measurement: taskSpec.taskSpec.execution?.measurement ?? null,
                observation: taskSpec.taskSpec.execution?.observation ?? null,
              },
            },
          };
          handleTaskSpecCreateOrUpdate(action.taskSpecId, updatedTaskSpecWrapper.taskSpec);
          handleTaskSpecClick(undefined, updatedTaskSpecWrapper, 'execution');
          if (updatedTreatmentList.length > 0) {
            successToast(`${treatmentsToRemove.size} measurement${treatmentsToRemove.size > 0 ? 's' : ''} removed`);
          }
        } else {
          errorToast('Could not remove treatment(s)');
        }
        setDisplayIssuesModal(false);
        break;
      }
      case 'remove-invalid-observations-and-open': {
        const taskSpec = taskSpecs.find((t) => t.id === action.taskSpecId);
        if (_notNil(taskSpec) && taskSpec.taskSpec.type === 'observation') {
          const observationsToRemove = new Set(action.observations);
          const updatedObservationList = (taskSpec.taskSpec.execution?.observation?.observations ?? []).filter(
            (observation) => !observationsToRemove.has(observation)
          );
          const updatedTaskSpecWrapper: TaskSpecWrapper = {
            ...taskSpec,
            taskSpec: {
              ...taskSpec.taskSpec,
              execution: {
                ...taskSpec.taskSpec.execution,
                type: 'workflow',
                observation: {
                  ...taskSpec.taskSpec.execution?.observation,
                  after_save_action: taskSpec.taskSpec.execution?.observation?.after_save_action ?? 'next_animal',
                  observations: updatedObservationList,
                },
                sample: taskSpec.taskSpec.execution?.sample ?? null,
                measurement: taskSpec.taskSpec.execution?.measurement ?? null,
                dosing: taskSpec.taskSpec.execution?.dosing ?? null,
              },
            },
          };
          handleTaskSpecCreateOrUpdate(action.taskSpecId, updatedTaskSpecWrapper.taskSpec);
          handleTaskSpecClick(undefined, updatedTaskSpecWrapper, 'execution');
          if (updatedObservationList.length > 0) {
            successToast(`${observationsToRemove.size} observation${observationsToRemove.size > 0 ? 's' : ''} removed`);
          }
        } else {
          errorToast('Could not remove observation(s)');
        }
        setDisplayIssuesModal(false);
        break;
      }
      default:
        errorToast('Something went wrong');
    }
  };

  return (
    <>
      {displayIssuesModal && (
        <ReactModal
          isOpen={true}
          className="ui-modal"
          style={{
            overlay: { backgroundColor: 'rgba(00,00,00,.5)' },
            content: { maxHeight: '100vh' },
          }}
          ariaHideApp={false}
        >
          <TaskSpecIssuesModal
            taskSpecTitleMap={taskSpecTitleMap}
            issues={taskSpecIssues}
            onAction={handleTaskSpecIssueAction}
          />
        </ReactModal>
      )}
      <div className="ui-card mv4 flex items-end flex-column" data-testid="tasks-table__container">
        {!isSummary && (
          <div className="w-100 flex justify-between items-center">
            <p className="ph3 lh-copy f6">
              <TaskSpecsBulkActions selectedTaskSpecs={selectedTaskSpecs} dispatch={dispatch} />
            </p>
            <div className="flex" style={{ alignItems: 'center' }}>
              {taskSpecIssues.size > 0 && (
                <Link className="link blue fl" openModal={true} onClick={() => setDisplayIssuesModal(true)}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <RiAlertFill className="red" size={25} />
                    <span className="pl2">Resolve {taskSpecIssues.size} conflicts to proceed</span>
                  </div>
                </Link>
              )}
              <Button className="ma3" onClick={handleAddClick}>
                Add Task
              </Button>
            </div>
          </div>
        )}
        <Table
          className="w-100 bt"
          columns={columns}
          data={taskSpecs}
          selectedRows={isSummary ? undefined : selectedRows}
          setSelectedRows={isSummary ? undefined : setSelectedRows}
          noDataMessage="Your tasks will appear here"
        />
      </div>
      {studyExecutionTimezoneEnabled ? (
        <div className="ui-card mt4">
          <div className="pa4">
            <div className="mb3">
              <h4 className="fw5">Study execution timezone</h4>
              <p>All tasks will be created and updated according to this timezone</p>
            </div>
            <div className="mb3" data-tooltip-id="global-tooltip-id" data-tooltip-content={timezoneValidation}>
              <TimezoneSelect
                timezones={timezones}
                disabled={isSummary}
                value={state.study.timezone}
                invalid={timezoneValidation !== null}
                onChange={handleStudyExecutionTimezoneUpdate}
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
